// Demo-data payloads (events catalog + availability trips) served by
// /api/v1/fleet/{variant}/events and /availability. Dummy data staged for
// customer demos — shapes mirror the eventual ClickHouse-backed tables.

export interface SeatEventImpact {
  event_id: string;
  event_time: string;
  duration: number;
  penalty: number;
  source_lru: string;
  description: string;
  /** Index into the trip's incidents list — drives what-if exclusions. */
  incident_idx?: number | null;
}

export interface TripSeat {
  availability_pct: number;
  /** Scoring divisor for the what-if recompute (0 → pinned at 100%). */
  total_minutes?: number;
  events: SeatEventImpact[];
}

export interface Incident {
  event_id: string;
  event_time: string;
  source_lru: string;
  penalty: number;
  duration: number;
  description: string;
  seats: string[];
  seat_count: number;
  impact: number;
}

export interface DemoTrip {
  id: string;
  name: string;
  date: string;
  label: string;
  avg_score: number;
  seats: Record<string, TripSeat>;
  incidents: Incident[];
}

export interface SiteTrendPoint {
  tripId: string;
  date: string;
  avg: number;
  nIncidents: number;
}

export interface SiteTopEvent {
  eventId: string;
  name: string;
  count: number;
  score: string;
  tripsAffected: number;
  seatsAffected: number;
  scenario: string;
  penalty: string;
}

export interface SiteWorstTrip {
  tripId: string;
  date: string;
  avg: number;
  nIncidents: number;
  worstSeat: string;
}

/** 30-day per-tail rollup — the Availability view's "Site" sub-mode. */
export interface SitePayload {
  tailId: string;
  fleetName: string;
  windowStart: string;
  windowEnd: string;
  windowDays: number;
  windowAvg: number;
  worstTripAvg: number;
  bestTripAvg: number;
  /** Direct jump targets for the worst/best stat tiles. */
  worstTripId?: string;
  bestTripId?: string;
  totalIncidents: number;
  trend: SiteTrendPoint[];
  topEvents: SiteTopEvent[];
  worstTrips: SiteWorstTrip[];
  /** Faults per seat over the window — the hotspot list. */
  seatFaultCount: Record<string, number>;
  /** Window-average score per seat — recolours the LOPA in Site mode. */
  seatAvgScore: Record<string, number>;
  scoreHistogram: { label: string; n: number }[];
}

/** One tail's demo history: the rollup + a full DemoTrip per trend point,
 *  so every trend bar / worst-trip row can deep-dive. */
export interface SiteBundle {
  tail: string;
  code?: string | null;
  site: SitePayload;
  trips: DemoTrip[];
}

export interface AvailabilityPayload {
  variant_id: string;
  sites: SiteBundle[];
}

export interface CatalogEvent {
  name: string;
  score: string;       // "Y" = scoring event, "N" = informational
  scope: string;
  scenario: string;
  penalty: string;
  category: string;    // Alarm / Alert / Flight / …
  type: string;
  location: string;    // layer bucket source (NC / Port / Seat / System / …)
}

export interface EventsPayload {
  variant_id: string;
  event_catalog: Record<string, CatalogEvent>;
  events_by_layer: Record<string, string[]>;
}

/** What-if simulation wiring passed down to the availability panels. */
export interface WhatIf {
  active: boolean;
  excluded: Set<number>;
  excludedBySeat: Map<string, Set<number>>;
  onToggleIncident: (idx: number) => void;
  onToggleSeatEvent: (seatId: string, idx: number) => void;
  onReset: () => void;
  /** As-recorded per-seat scores (pre-simulation), for the footnote. */
  actualSeats: Record<string, TripSeat>;
}


/** Availability score → seat colour. Mirrors the generator's availColor()
 *  exactly (green 100 → yellow 85 → orange 70 → red 50 → dark red). */
export function availColor(pct: number | undefined): string {
  if (pct === undefined || pct === null) return "#B8C2CE";
  if (pct >= 100) return "#27AE60";
  if (pct >= 95)  return "#58D68D";
  if (pct >= 85)  return "#F4D03F";
  if (pct >= 70)  return "#E67E22";
  if (pct >= 50)  return "#E74C3C";
  return "#922B21";
}
